import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMoneyBillWave,
    faPercentage,
    faHandshake,
    faBuilding,
    faUsers,
    faChartPie,
    faSyncAlt,
    faCalendarAlt,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

// Adaptez l'URL selon votre structure
const API_URL = `${API_CONFIG2.apiUrl}factures/api_revenue_etat.php`;

const FinancialStatements2 = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [mois, setMois] = useState(''); // vide = tous les mois

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const url = new URL(API_URL);
            url.searchParams.append('annee', annee);
            if (mois) url.searchParams.append('mois', mois);
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const json = await response.json();
            setData(json);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [annee, mois]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '0 USD';
        return new Intl.NumberFormat('fr-CD', { style: 'currency', currency: 'USD' }).format(amount);
    };

    if (loading && !data) {
        return (
            <div className="revenue-etat-wrapper">
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="text-secondary mt-2">Chargement des données fiscales...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="revenue-etat-wrapper">
                <div className="text-center py-4 text-danger">
                    <FontAwesomeIcon icon={faExclamationTriangle} size="2x" className="mb-2" />
                    <p>{error}</p>
                    <button className="btn-glass" onClick={fetchData}>Réessayer</button>
                </div>
            </div>
        );
    }

    const d = data || {};

    return (
        <>
            <style>{`
                .revenue-etat-wrapper {
                    background: radial-gradient(ellipse at 20% 30%, #1e293b, #0f172a);
                    padding: 1.5rem;
                    border-radius: 24px;
                    min-height: 60vh;
                    margin-top: 1.5rem;
                }
                .header-revenue {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    flex-wrap: wrap;
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }
                .header-revenue h4 {
                    font-weight: 700;
                    color: #e2e8f0;
                    margin: 0;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }
                .filters {
                    display: flex;
                    gap: 0.5rem;
                    align-items: center;
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.06);
                    border-radius: 40px;
                    padding: 0.2rem 0.8rem;
                }
                .filters label {
                    color: #94a3b8;
                    margin: 0;
                }
                .filters select {
                    background: transparent;
                    border: none;
                    color: #e2e8f0;
                    padding: 0.2rem 0;
                    outline: none;
                }
                .btn-glass {
                    background: rgba(255,255,255,0.04);
                    border: 1px solid rgba(255,255,255,0.06);
                    color: #e2e8f0;
                    border-radius: 40px;
                    padding: 0.4rem 1rem;
                    transition: all 0.2s;
                    font-size: 0.9rem;
                    backdrop-filter: blur(4px);
                    cursor: pointer;
                }
                .btn-glass:hover { background: rgba(255,255,255,0.08); }
                .btn-glass:disabled { opacity: 0.5; cursor: not-allowed; }

                .kpi-grid-revenue {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
                    gap: 1.5rem;
                    margin-bottom: 2rem;
                }
                .kpi-card-revenue {
                    background: rgba(255,255,255,0.03);
                    backdrop-filter: blur(8px);
                    border: 1px solid rgba(255,255,255,0.06);
                    border-radius: 20px;
                    padding: 1.5rem 1rem;
                    text-align: center;
                    transition: all 0.3s ease;
                }
                .kpi-card-revenue:hover {
                    background: rgba(255,255,255,0.06);
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(0,0,0,0.2);
                }
                .kpi-card-revenue .kpi-icon {
                    font-size: 2rem;
                    display: block;
                    margin-bottom: 0.5rem;
                }
                .kpi-card-revenue .kpi-value {
                    font-size: 1.4rem;
                    font-weight: 700;
                    color: #fff;
                }
                .kpi-card-revenue .kpi-label {
                    font-size: 0.85rem;
                    color: #94a3b8;
                    margin-top: 0.3rem;
                }
                .kpi-total {
                    background: rgba(59,130,246,0.15);
                    border-color: rgba(59,130,246,0.3);
                }
                .kpi-total .kpi-value {
                    color: #60a5fa;
                }
                @media (max-width: 768px) {
                    .revenue-etat-wrapper { padding: 1rem; }
                    .kpi-grid-revenue { grid-template-columns: 1fr 1fr; gap: 1rem; }
                    .header-revenue { flex-direction: column; align-items: stretch; }
                    .filters { width: 100%; justify-content: center; }
                }
            `}</style>

            <div className="revenue-etat-wrapper">
                <div className="header-revenue">
                    <h4>
                        <FontAwesomeIcon icon={faChartPie} className="icon" />
                        État des Revenus (Fiscaux & Salariaux)
                    </h4>
                    <div className="d-flex gap-2 align-items-center flex-wrap">
                        <div className="filters">
                            <FontAwesomeIcon icon={faCalendarAlt} className="text-secondary" />
                            <label>Année</label>
                            <select value={annee} onChange={(e) => setAnnee(parseInt(e.target.value))}>
                                {[2023, 2024, 2025, 2026].map(y => (
                                    <option key={y} value={y}>{y}</option>
                                ))}
                            </select>
                        </div>
                        <div className="filters">
                            <label>Mois</label>
                            <select value={mois} onChange={(e) => setMois(e.target.value)}>
                                <option value="">Tous</option>
                                {Array.from({length:12}, (_,i) => i+1).map(m => (
                                    <option key={m} value={m}>{m}</option>
                                ))}
                            </select>
                        </div>
                        <button className="btn-glass" onClick={fetchData} disabled={loading}>
                            <FontAwesomeIcon icon={faSyncAlt} spin={loading} className="me-1" />
                            Actualiser
                        </button>
                    </div>
                </div>

                <div className="kpi-grid-revenue">
                    <div className="kpi-card-revenue">
                        <FontAwesomeIcon icon={faPercentage} className="kpi-icon" style={{ color: '#f59e0b' }} />
                        <div className="kpi-value">{formatCurrency(d.total_tva)}</div>
                        <div className="kpi-label">TVA collectée</div>
                    </div>
                    <div className="kpi-card-revenue">
                        <FontAwesomeIcon icon={faHandshake} className="kpi-icon" style={{ color: '#3b82f6' }} />
                        <div className="kpi-value">{formatCurrency(d.total_ipr)}</div>
                        <div className="kpi-label">IPR</div>
                    </div>
                    <div className="kpi-card-revenue">
                        <FontAwesomeIcon icon={faBuilding} className="kpi-icon" style={{ color: '#22c55e' }} />
                        <div className="kpi-value">{formatCurrency(d.total_cnss_salarial)}</div>
                        <div className="kpi-label">CNSS Salarial</div>
                    </div>
                    <div className="kpi-card-revenue">
                        <FontAwesomeIcon icon={faUsers} className="kpi-icon" style={{ color: '#ec4899' }} />
                        <div className="kpi-value">{formatCurrency(d.total_inpp_salarial)}</div>
                        <div className="kpi-label">INPP Salarial</div>
                    </div>
                    <div className="kpi-card-revenue">
                        <FontAwesomeIcon icon={faUsers} className="kpi-icon" style={{ color: '#8b5cf6' }} />
                        <div className="kpi-value">{formatCurrency(d.total_onem_salarial)}</div>
                        <div className="kpi-label">ONEM Salarial</div>
                    </div>
                    <div className="kpi-card-revenue kpi-total">
                        <FontAwesomeIcon icon={faMoneyBillWave} className="kpi-icon" style={{ color: '#60a5fa' }} />
                        <div className="kpi-value">{formatCurrency(d.total_general)}</div>
                        <div className="kpi-label">Total Général</div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FinancialStatements2;